import {
	Box,
	CircularProgress,
	Divider,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { StackerBundle, StackerBundleDraft } from "../../models/StackerBundle";
import { formatWeight } from "../../utils/utils";
import { DisplayPiecesRow, EditPiecesRow } from "./StackerPiecesRow";

type Props = {
	rows: StackerBundle[];
	shiftId: number;
	draftRow: StackerBundleDraft | null;
	editingId: number | null;
	isMutating: boolean;
	isTotalsLoading: boolean;
	readOnly?: boolean;
	onStartEdit: (bundle: StackerBundle) => void;
	onCancelEdit: () => void;
	onSaveDraft: (draft: StackerBundleDraft) => void;
	onDelete: (bundle: StackerBundle) => void;
	onDraftChange: (draft: StackerBundleDraft) => void;
	onQuickSave: (
		bundle: StackerBundle,
		patch: {
			GoodPieces: number;
			ScrapPieces: number | null;
			TotalWeight: number | null;
		},
	) => void;
};

export const StackerPiecesGrid = ({
	rows,
	draftRow,
	editingId,
	isMutating,
	isTotalsLoading,
	readOnly = false,
	onStartEdit,
	onCancelEdit,
	onDraftChange,
	onSaveDraft,
	onDelete,
	onQuickSave,
}: Props) => {
	// Footer totals reflect the persisted (server) values, not live row edits.
	const goodPieces = rows.reduce((sum, r) => sum + (r.GoodPieces ?? 0), 0);
	const totalPieces = rows.reduce(
		(sum, r) => sum + (r.GoodPieces ?? 0) + (r.ScrapPieces ?? 0),
		0,
	);
	const totalWeight = rows.reduce((sum, r) => sum + (r.TotalWeight || 0), 0);

	return (
		<Paper elevation={2} sx={{ overflow: "hidden" }}>
			<Box
				sx={{
					px: 2.5,
					py: 1.5,
					borderBottom: "1px solid",
					borderColor: "divider",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "baseline",
				}}
			>
				<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
					Rolled Bars
				</Typography>
			</Box>

			<TableContainer>
				<Table
					size="small"
					sx={{
						tableLayout: "fixed",
						width: "100%",
						"& .MuiTableHead-root .MuiTableCell-root": {
							fontSize: (theme) => theme.typography.caption.fontSize,
							fontWeight: 600,
							color: (theme) => theme.palette.text.secondary,
						},
						"& .MuiTableBody-root .MuiTableCell-root": {
							fontSize: (theme) => theme.typography.body2.fontSize,
							fontWeight: 400,
						},
					}}
				>
					<TableHead>
						<TableRow>
							<TableCell sx={{ width: "2%" }}>#</TableCell>
							<TableCell sx={{ width: "12%" }}>Section</TableCell>
							<TableCell sx={{ width: "11%" }}>Heat</TableCell>
							<TableCell sx={{ width: "11%" }}>Grade</TableCell>
							<TableCell sx={{ width: "11%" }}>Length</TableCell>
							<TableCell sx={{ width: "11%" }}>Good Pieces</TableCell>
							<TableCell sx={{ width: "10%" }}>Total Weight</TableCell>
							<TableCell sx={{ width: "10%" }}>Scrap Pieces</TableCell>
							<TableCell sx={{ width: "17%" }}>Reason for Scrap</TableCell>
							<TableCell align="right" sx={{ width: "5%" }}>
								Actions
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map((row, idx) => {
							const isEditing =
								editingId === row.StackerBundleId && draftRow !== null;
							if (isEditing && draftRow) {
								return (
									<EditPiecesRow
										key={`edit-${row.StackerBundleId}`}
										idx={idx + 1}
										draft={draftRow}
										original={row}
										isMutating={isMutating}
										onChange={onDraftChange}
										onSave={() => onSaveDraft(draftRow)}
										onCancel={onCancelEdit}
									/>
								);
							}
							return (
								<DisplayPiecesRow
									key={row.StackerBundleId}
									idx={idx + 1}
									row={row}
									disabled={editingId !== null || readOnly}
									onEdit={() => onStartEdit(row)}
									onDelete={() => onDelete(row)}
									onQuickSave={onQuickSave}
								/>
							);
						})}

						{draftRow && draftRow.StackerBundleId === null && (
							<EditPiecesRow
								key="new-draft"
								idx={rows.length + 1}
								draft={draftRow}
								isMutating={isMutating}
								onChange={onDraftChange}
								onSave={() => onSaveDraft(draftRow)}
								onCancel={onCancelEdit}
							/>
						)}

						{rows.length === 0 && !draftRow && (
							<TableRow>
								<TableCell colSpan={10} sx={{ py: 4, textAlign: "center" }}>
									<Typography variant="body2" color="text.secondary">
										No pieces for this shift yet. Click <b>+ Add Row</b> to
										enter piece counts.
									</Typography>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			<Box
				sx={{
					borderTop: "1px solid",
					borderColor: "divider",
					backgroundColor: "background.paper",
					display: "flex",
					justifyContent: "flex-end",
				}}
			>
				<Box
					sx={{
						display: "inline-flex",
						alignItems: "center",
						flexWrap: "wrap",
						columnGap: 3,
						rowGap: 1,
						px: 2.5,
						py: 1.25,
						backgroundColor: "background.paper",
					}}
				>
					<Divider orientation="vertical" flexItem />
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ fontWeight: 500, mr: 0.25 }}
						>
							Total Weight:
						</Typography>
						{isTotalsLoading ? (
							<CircularProgress size={18} thickness={5} />
						) : (
							<Typography
								variant="h6"
								color="primary.main"
								sx={{ fontWeight: 500, fontVariantNumeric: "tabular-nums" }}
							>
								{formatWeight(totalWeight)}
							</Typography>
						)}
					</Box>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ fontWeight: 500, mr: 0.25 }}
						>
							Pieces:
						</Typography>
						{isTotalsLoading ? (
							<CircularProgress size={18} thickness={5} />
						) : (
							<>
								<Typography
									variant="h6"
									color="success.main"
									sx={{ fontWeight: 500, fontVariantNumeric: "tabular-nums" }}
								>
									{goodPieces.toLocaleString()}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									good
								</Typography>
								<Typography
									variant="h6"
									color="text.disabled"
									sx={{ fontWeight: 300, mx: 0.25 }}
								>
									/
								</Typography>
								<Typography
									variant="h6"
									color="primary.main"
									sx={{ fontWeight: 500, fontVariantNumeric: "tabular-nums" }}
								>
									{totalPieces.toLocaleString()}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									total
								</Typography>
							</>
						)}
					</Box>
				</Box>
			</Box>
		</Paper>
	);
};
